import FeatureFlag from "@/domain/enums/FeatureFlag";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import { useServiceContext } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useGetEventBus from "./useGetEventBus";

const useFeatureFlag = () => {
    const { sdk, isInitialized: isServicesInitialized } = useServiceContext();
    const userManager = sdk?.userManager;
    const queryClient = useQueryClient();

    const useFeatureFlagQuery = (featureFlag: FeatureFlag) =>
        useQuery({
            queryKey: queryKeys.featureFlag.detail(featureFlag),
            queryFn: async () => {
                if (!isServicesInitialized || !userManager) {
                    throw new Error(
                        "Services not initialized or UserManager undefined",
                    );
                }

                try {
                    return userManager.isFeatureFlagEnabled(featureFlag);
                } catch (error) {
                    console.error(
                        `Error checking feature flag ${featureFlag}:`,
                        error,
                    );
                    throw error;
                }
            },
            enabled: isServicesInitialized && !!userManager,
        });

    const isFeatureFlagEnabled = (featureFlag: FeatureFlag): boolean => {
        const { data, isLoading, error } = useFeatureFlagQuery(featureFlag);

        if (isLoading || error || data === undefined) {
            return false;
        }

        return data;
    };

    const handleFeatureFlagUpdated = () => {
        queryClient.invalidateQueries({
            queryKey: queryKeys.featureFlag.all(),
        });
    };

    const { off: offFeatureFlagUpdated } = useGetEventBus(
        eventKeys.featureFlag.updated,
        handleFeatureFlagUpdated,
    );

    const signatureSettingsFeatureFlagQuery = useFeatureFlagQuery(
        FeatureFlag.SETTINGS_DISPLAY_SIGNATURE,
    );

    useEffect(
        () => () => {
            offFeatureFlagUpdated(
                eventKeys.featureFlag.updated,
                handleFeatureFlagUpdated,
            );
        },
        [],
    );

    return {
        useFeatureFlagQuery,
        isFeatureFlagEnabled,
        isLicenseManagementDisplayed: isFeatureFlagEnabled(
            FeatureFlag.SETTINGS_DISPLAY_LICENSEMANAGEMENT,
        ),
        isAdminDashboardDisplayed: isFeatureFlagEnabled(
            FeatureFlag.SETTINGS_DISPLAY_ADMINDASHBOARD,
        ),
        isModelManagementDisplayed: isFeatureFlagEnabled(
            FeatureFlag.SETTINGS_DISPLAY_MODELMANAGEMENT,
        ),
        isSignatureSettingsTabVisible:
            !signatureSettingsFeatureFlagQuery.isLoading &&
            signatureSettingsFeatureFlagQuery.data === true,
        isWelcomeVideoDisplayed: isFeatureFlagEnabled(
            FeatureFlag.ONBOARDING_DISPLAY_WELCOMEVIDEO,
        ),
        isNewFeatureDisplayed: isFeatureFlagEnabled(
            FeatureFlag.ONBOARDING_DISPLAY_NEWFEATURE,
        ),
        isMailDeepFocusEnabled: isFeatureFlagEnabled(
            FeatureFlag.MAIL_ENABLE_DEEPFOCUS,
        ),
        isMailAnalysisEnabled: isFeatureFlagEnabled(
            FeatureFlag.MAIL_ENABLE_ANALYSIS,
        ),
        isAssistantVocalEnabled: isFeatureFlagEnabled(
            FeatureFlag.ASSISTANT_ENABLE_VOCAL,
        ),
        isAssistantPromptEnabled: isFeatureFlagEnabled(
            FeatureFlag.ASSISTANT_ENABLE_PROMPT,
        ),
        isAgendaAccessEnabled: isFeatureFlagEnabled(
            FeatureFlag.AGENDA_ENABLE_ACCESS,
        ),
        isDeviceStorageAccessEnabled: isFeatureFlagEnabled(
            FeatureFlag.DEVICESTORAGE_ENABLE_ACCESS,
        ),
        isDashboardCO2Displayed: isFeatureFlagEnabled(
            FeatureFlag.DASHBOARD_DISPLAY_CO2,
        ),
        isSystemLogsEnabled: isFeatureFlagEnabled(
            FeatureFlag.SYSTEM_ENABLE_LOGS,
        ),
        isSupportTicketCreationEnabled: isFeatureFlagEnabled(
            FeatureFlag.SUPPORT_ENABLE_TICKETCREATION,
        ),
        isCustomViewCreationEnabled: isFeatureFlagEnabled(
            FeatureFlag.CUSTOMVIEW_ENABLE_CREATION,
        ),
    };
};

export default useFeatureFlag;
